/**
 * useRunStatus (AC-1365)
 *
 * The single source of truth for "is this run instance still running?" in the
 * UI. Treats the client view as a CACHE of server state, not authoritative,
 * which is the principle that prevents the stuck-spinner class of bugs.
 *
 * Behavior:
 *   - Connects an SSE stream when given a runInstanceId. The server sends a
 *     snapshot first, then every status transition; the hook updates state
 *     synchronously as events arrive.
 *   - Revalidates against the REST endpoint on mount, on window focus, and
 *     on tab visibility change. This catches drift if SSE was missed (e.g.
 *     the connection was opened after the run already finished and the
 *     snapshot was lost to a transient error).
 *   - Falls back to a single REST read if SSE errors out repeatedly. Polling
 *     is intentionally NOT done here; the workflow store still owns polling
 *     for run RESULTS, which is a different concern.
 *
 * Returns null status until the first revalidate or SSE event lands; callers
 * should treat null as "unknown, don't render terminal UI yet."
 */
import { useEffect, useRef, useState } from 'react';
import apiService from '../services/api';
import { openRunStatusStream } from '../services/runStatusStream';
import type { RunInstance, RunStatus } from '../types';

const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set<RunStatus>([
  'completed',
  'failed',
  'stopped',
]);

const SSE_FAILURE_THRESHOLD = 3;

export interface UseRunStatusResult {
  status: RunStatus | null;
  isRunning: boolean;
  isPaused: boolean;
  isTerminal: boolean;
  runInstance: RunInstance | null;
  error: string | null;
  /** Force a revalidate via REST, e.g. after a user action. */
  revalidate: () => Promise<void>;
}

export function useRunStatus(
  runInstanceId: number | string | null | undefined,
): UseRunStatusResult {
  const [status, setStatus] = useState<RunStatus | null>(null);
  const [runInstance, setRunInstance] = useState<RunInstance | null>(null);
  const [error, setError] = useState<string | null>(null);

  // We keep these in refs so async callbacks always see the latest id without
  // re-creating the SSE stream on every render.
  const idRef = useRef<string | null>(null);
  idRef.current = runInstanceId != null ? String(runInstanceId) : null;

  // Pin a "current run" key per effect run so late-arriving callbacks from a
  // previous run can be ignored. Without this a stale revalidate could
  // overwrite state for the new run.
  useEffect(() => {
    const id = runInstanceId != null ? String(runInstanceId) : null;
    if (!id) {
      setStatus(null);
      setRunInstance(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const applyRunInstance = (ri: RunInstance) => {
      if (cancelled) return;
      setRunInstance(ri);
      setStatus(ri.status);
    };

    const revalidate = async () => {
      if (cancelled) return;
      try {
        const ri = await apiService.getRunInstance(id);
        applyRunInstance(ri);
        setError(null);
      } catch (err) {
        // 401 is handled by the axios interceptor (refresh + retry); anything
        // that reaches here is either a hard auth failure or a real server
        // error. Surface it without clobbering existing state.
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to read run status');
      }
    };

    // Mount-time revalidate. Even though SSE will emit a snapshot, doing this
    // in parallel means the hook surfaces a status faster on slow networks
    // and survives a momentary SSE failure.
    revalidate();

    // SSE stream for live updates. We don't track snapshot vs status events
    // separately - the snapshot is just the first status event for our
    // purposes.
    const stream = openRunStatusStream(
      id,
      () => apiService.getAccessToken(),
      {
        onStatus: (event) => {
          if (cancelled) return;
          if (event.status) setStatus(event.status);
          // The SSE payload has fewer fields than RunInstance; refresh the
          // full record on terminal transitions so callers that read
          // runInstance.summary etc. see the final value.
          if (event.status && TERMINAL_STATUSES.has(event.status)) {
            revalidate();
          }
        },
        onTerminal: () => {
          // Stream closed cleanly; SSE wrapper handles teardown.
        },
        onError: (err, retryCount) => {
          if (cancelled) return;
          // Don't surface transient SSE errors to the caller - the wrapper
          // is reconnecting. After several failures, fall back to a single
          // revalidate so the UI doesn't sit on stale state forever.
          if (retryCount >= SSE_FAILURE_THRESHOLD) {
            revalidate();
          }
        },
      },
    );

    // Revalidate when the tab regains focus. This is the safety net that
    // catches anything SSE missed (browser tab throttling, sleep, etc.).
    const onFocus = () => revalidate();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stream.stop();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [runInstanceId]);

  return {
    status,
    isRunning: status === 'running',
    isPaused: status === 'paused' || status === 'waiting_for_input',
    isTerminal: status != null && TERMINAL_STATUSES.has(status),
    runInstance,
    error,
    revalidate: async () => {
      const id = idRef.current;
      if (!id) return;
      try {
        const ri = await apiService.getRunInstance(id);
        setRunInstance(ri);
        setStatus(ri.status);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to read run status');
      }
    },
  };
}
